import React, { useEffect, useState } from "react";
import { Check, Copy, Info, RefreshCw, X } from "lucide-react";
import { useSocket } from "../../providers/SocketProvider";

const CLIENT_SEED_PATTERN = /^[A-Za-z0-9_-]{8,128}$/;

const ProvablyFairSettingsModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const {
    clientSeed,
    generateNewSeed,
    changeClientSeed,
    serverSeedHash,
  } = useSocket();
  const [seedMode, setSeedMode] = useState<"random" | "manual">("random");
  const [manualSeed, setManualSeed] = useState("");
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setManualSeed(clientSeed);
    setError("");
    setCopied(false);
  }, [clientSeed, isOpen]);

  const copyClientSeed = async () => {
    try {
      await navigator.clipboard.writeText(clientSeed);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setError("Unable to copy the client seed.");
    }
  };

  const applyRandomSeed = () => {
    generateNewSeed();
    setSeedMode("random");
    setError("");
  };

  const applyManualSeed = () => {
    const normalizedSeed = manualSeed.trim();
    if (!CLIENT_SEED_PATTERN.test(normalizedSeed)) {
      setError(
        "Use 8–128 characters containing only letters, numbers, _ or -.",
      );
      return;
    }

    changeClientSeed(normalizedSeed);
    setError("");
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-70 z-[1600] flex justify-center items-start sm:p-7 p-2"
      onClick={onClose}
    >
      <div
        className="bg-[#1b1c1d] w-full max-w-[800px] rounded-xl text-white shadow-xl relative overflow-hidden"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex justify-between items-center px-5 py-3 border-b border-[#2c2d30] bg-[#242424]">
          <span className="text-base font-semibold">PROVABLY FAIR SETTINGS</span>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X size={20} />
          </button>
        </div>

        <div className="px-5 py-4 text-sm text-[#9b9c9e] border-b border-[#2c2d30]">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <p>
              The committed server seed and your client seed are combined to
              determine the round result. The server seed is revealed in round
              history so its SHA-256 commitment can be verified.
            </p>
            <button
              type="button"
              onClick={() => setShowExplanation((previous) => !previous)}
              className="text-red-400 whitespace-nowrap flex items-center gap-1"
            >
              <Info size={15} /> What is Provably Fair
            </button>
          </div>
          {showExplanation && (
            <div className="mt-3 rounded-lg bg-black/40 p-3 text-xs leading-5">
              Before a round, the server publishes only a hash of its secret
              seed. After the round, the revealed seed can be hashed again and
              compared with that commitment. This prevents the server from
              changing the committed seed after bets are placed.
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-b border-[#2c2d30]">
          <div className="flex items-center gap-2 mb-1">
            <span aria-hidden="true">🖥️</span>
            <span className="text-sm">Client (your) seed:</span>
          </div>
          <p className="text-xs text-[#9b9c9e] mb-3">
            Changing the seed reconnects the game so subsequent rounds use the
            selected value.
          </p>

          <label className="flex flex-col lg:flex-row lg:items-center gap-3 bg-[#151616] rounded-lg p-3 mb-3 cursor-pointer">
            <div className="flex items-center gap-2 min-w-[190px] text-sm text-[#9b9c9e]">
              <input
                type="radio"
                name="client-seed-mode"
                checked={seedMode === "random"}
                onChange={() => setSeedMode("random")}
                className="accent-green-600"
              />
              Generate a random seed
            </div>
            <div className="flex-1 min-w-0 bg-black rounded-full px-4 py-2 flex items-center gap-2">
              <span className="font-mono text-sm text-white truncate flex-1">
                {clientSeed}
              </span>
              <button
                type="button"
                onClick={(event) => {
                  event.preventDefault();
                  copyClientSeed();
                }}
                className="text-gray-400 hover:text-white"
                aria-label="Copy client seed"
              >
                {copied ? <Check size={16} /> : <Copy size={16} />}
              </button>
            </div>
            <button
              type="button"
              onClick={(event) => {
                event.preventDefault();
                applyRandomSeed();
              }}
              className="h-9 px-4 rounded-full border border-[#b2f2a3] bg-[#28a909] text-sm flex items-center justify-center gap-2"
            >
              <RefreshCw size={15} /> GENERATE
            </button>
          </label>

          <label className="flex flex-col lg:flex-row lg:items-center gap-3 bg-[#151616] rounded-lg p-3 cursor-pointer">
            <div className="flex items-center gap-2 min-w-[190px] text-sm text-[#9b9c9e]">
              <input
                type="radio"
                name="client-seed-mode"
                checked={seedMode === "manual"}
                onChange={() => setSeedMode("manual")}
                className="accent-green-600"
              />
              Enter manually
            </div>
            <input
              value={manualSeed}
              onFocus={() => setSeedMode("manual")}
              onChange={(event) => setManualSeed(event.target.value)}
              disabled={seedMode !== "manual"}
              placeholder="Enter client seed"
              className="flex-1 min-w-0 bg-black rounded-full px-4 py-2 font-mono text-sm text-white outline-none disabled:opacity-40"
            />
            <button
              type="button"
              onClick={applyManualSeed}
              disabled={seedMode !== "manual"}
              className="h-9 px-5 rounded-full border border-[#b2f2a3] bg-[#28a909] text-sm disabled:opacity-40"
            >
              CHANGE
            </button>
          </label>

          {error && <p className="text-red-400 text-xs mt-2">{error}</p>}
        </div>

        <div className="px-5 py-4">
          <div className="flex items-center gap-2 mb-3 text-sm">
            <span aria-hidden="true">🖧</span>
            <span>Server seed SHA-256 commitment:</span>
          </div>
          <div className="bg-black text-white font-mono px-4 py-3 rounded-full break-all text-sm min-h-[44px]">
            {serverSeedHash || (
              <span className="font-sans text-[#7b7b7b]">
                Waiting for the server commitment…
              </span>
            )}
          </div>
        </div>

        <div className="px-5 py-4 bg-[#242424] text-center text-sm text-[#7b7b7b] border-t border-[#2c2d30]">
          Revealed seeds and verification are available from round history.
        </div>
      </div>
    </div>
  );
};

export default ProvablyFairSettingsModal;
